import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, ArrowLeft, CheckCircle, AlertCircle } from 'lucide-react';
import api from '../../utils/api';

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!email) {
      setError('Please enter your email address');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setIsLoading(true);

    try {
      const response = await api.post('/api/auth/forgot-password', { email });

      if (response.data.message) {
        setSuccess(true);
      } else {
        setError('Failed to send reset email. Please try again.');
      }
    } catch (error) {
      console.error('Forgot password error:', error);

      if (error.response?.status === 404) {
        setError('No account found with this email address');
      } else if (error.response?.status === 429) {
        setError('Too many requests. Please wait before trying again.');
      } else {
        setError('An error occurred. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    setEmail(e.target.value);
    if (error) setError(null);
  };

  const seo = (
    <Helmet>
      <title>Forgot Password | Receevit®</title>
      <meta name="description" content="Reset your Receevit® password securely. Enter your email to receive reset instructions." />
      <link rel="canonical" href="https://www.receevit.com/forgot-password" />
      <meta property="og:title" content="Forgot Password | Receevit®" />
      <meta property="og:description" content="Reset your Receevit® password securely. Enter your email to receive reset instructions." />
      <meta property="og:url" content="https://www.receevit.com/forgot-password" />
      <meta property="og:type" content="website" />
      <meta property="og:image" content="https://www.receevit.com/og-image.png" />
    </Helmet>
  );

  if (success) {
    return (
      <div className="flex flex-col items-center justify-center py-10 px-4 sm:px-6 lg:px-8 w-full max-w-7xl mx-auto flex-1">
        {seo}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45 }}
          className="w-full max-w-md space-y-6"
        >
          <div className="text-center">
            <CheckCircle className="mx-auto h-14 w-14 text-green-400 mb-4" />
            <h1 className="text-2xl font-bold text-white mb-2">Check your email</h1>
            <p className="text-blue-100 text-sm">
              We&apos;ve sent a password reset link. Check your inbox and spam folder.
            </p>
          </div>
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, delay: 0.05 }}
            className="bg-white rounded-3xl shadow-2xl p-8"
          >
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <div className="flex items-center justify-center gap-2">
                <Mail className="h-5 w-5 text-green-600 shrink-0" />
                <p className="text-sm text-green-800 text-left">
                  The reset link expires in 1 hour for your security.
                </p>
              </div>
            </div>
            <div className="space-y-3">
              <Link
                to="/login"
                className="block w-full text-center bg-[#1F11ED] text-white py-3 px-6 rounded-xl font-semibold hover:bg-blue-700 transition-all duration-200"
              >
                Back to login
              </Link>
              <button
                type="button"
                onClick={() => {
                  setSuccess(false);
                  setEmail('');
                }}
                className="w-full bg-gray-100 text-gray-700 py-3 px-6 rounded-xl font-semibold hover:bg-gray-200 transition-all duration-200"
              >
                Try another email
              </button>
            </div>
          </motion.div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center py-10 px-4 sm:px-6 lg:px-8 w-full max-w-7xl mx-auto flex-1">
      {seo}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45 }}
        className="w-full max-w-md space-y-6"
      >
        <div className="text-center">
          <h1 className="text-3xl font-bold text-white mb-2">Forgot password</h1>
          <p className="text-blue-100 text-sm">
            Enter your email and we&apos;ll send a secure reset link
          </p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, delay: 0.05 }}
          className="bg-white rounded-3xl shadow-2xl p-8"
        >
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-red-50 border border-red-200 rounded-lg p-4"
              >
                <div className="flex">
                  <AlertCircle className="h-5 w-5 text-red-400 shrink-0" />
                  <div className="ml-3">
                    <p className="text-sm text-red-800">{error}</p>
                  </div>
                </div>
              </motion.div>
            )}

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#1F11ED] focus:border-[#1F11ED] transition-all duration-200"
                placeholder="Enter your email address"
              />
            </div>

            <button
              type="submit"
              disabled={isLoading || !email}
              className="w-full bg-[#1F11ED] text-white py-3 px-6 rounded-xl font-semibold hover:bg-blue-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Sending reset link...' : 'Send reset link'}
            </button>

            <div className="text-center">
              <Link
                to="/login"
                className="inline-flex items-center text-sm text-[#1F11ED] hover:text-blue-700 font-medium transition-colors duration-200"
              >
                <ArrowLeft className="h-4 w-4 mr-1" />
                Back to login
              </Link>
            </div>
          </form>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default ForgotPasswordPage;
